/**
 * Customer Slide Details — tenant-scoped read-only view.
 * No QC, artifacts, attribution, job id, or pipeline diagnostics.
 */
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import ZoomableImage from "../components/ZoomableImage";
import InfoTip from "../components/InfoTip";
import { authedGet } from "../api/customerAuthedFetch";
import adminStyles from "../ui/AdminStyles";

const PAGE_TITLE = "Slide Details";

const TIPS = {
  application: "Catalog application that processed this slide.",
  origin: "How the slide entered the pipeline (for example portal upload or partner integration).",
  received: "When processing started for this job (timezone selected above).",
  finished: "When processing finished for this job (timezone selected above).",
  duration: "Elapsed processing time from start to finish.",
  viewer:
    "Opens AIxMed Cytology Viewer for this slide when a viewer URL is available. Requires the Viewer role.",
  timezone: "Timezone used to display Received / Finished timestamps.",
  status: "Pipeline outcome for this job.",
  message: "Last status details from processing.",
  suspicious: "Counts and morphology metrics for cells classified as Suspicious.",
  atypical: "Counts and morphology metrics for cells classified as Atypical.",
  fileSize: "Approximate size of the input slide file in megabytes.",
  layers: "Number of image layers / focus planes recorded for this slide.",
  mpp: "Microns per pixel (image resolution metadata).",
  magnification: "Optical magnification associated with the scan, when known.",
  scanner: "Scanner model or vendor reported for the slide, when known.",
  med: "AIxMed MED package format version produced during processing.",
  hash: "Content hash of the input file used for duplicate / integrity correlation.",
};

function formatTimestamp(ts, timeZone) {
  if (!ts) return "—";
  const d = new Date(ts);
  if (isNaN(d)) return "—";
  return d
    .toLocaleString("sv-SE", {
      timeZone,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    })
    .replace("T", " ");
}

function formatMagnification(value) {
  if (value == null || value === "") return "—";
  const n = Number(value);
  if (!Number.isFinite(n) || n <= 0) return "—";
  return `${n}X`;
}

function formatMedFormatVersion(value) {
  if (value == null || value === "") return "—";
  const n = Number(value);
  if (!Number.isFinite(n) || n <= 0) return "—";
  return `v${n}`;
}

function formatDurationHuman(seconds) {
  seconds = Math.floor(seconds);
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = seconds % 60;
  const parts = [];
  if (h > 0) parts.push(`${h} hr`);
  if (m > 0 || h > 0) parts.push(`${m} min`);
  if ((h === 0 && m === 0) || s > 0) parts.push(`${s} sec`);
  return parts.join(" ");
}

function StatusBadge({ status, size = "normal" }) {
  const styleMap = {
    finished: { text: "#1b5e20", bg: "#c8e6c9" },
    running: { text: "#0d47a1", bg: "#bbdefb" },
    queued: { text: "#e65100", bg: "#ffe0b2" },
    failed: { text: "#b71c1c", bg: "#ffcdd2" },
    aborted: { text: "#424242", bg: "#e0e0e0" },
    unknown: { text: "#424242", bg: "#eeeeee" },
  };
  const sizeStyles = {
    normal: { fontSize: 14, padding: "4px 10px", borderRadius: 12, fontWeight: 600 },
    large: { fontSize: 18, padding: "6px 14px", borderRadius: 14, fontWeight: 700 },
  };
  const key = status?.toLowerCase() || "unknown";
  const colors = styleMap[key] || styleMap.unknown;
  return (
    <span
      style={{
        color: colors.text,
        backgroundColor: colors.bg,
        textTransform: "capitalize",
        whiteSpace: "nowrap",
        ...sizeStyles[size],
      }}
    >
      {status || "Unknown"}
    </span>
  );
}

export default function CustomerSlideDetail() {
  const { jobId } = useParams();
  const navigate = useNavigate();
  const ALL_TIMEZONES = Intl.supportedValuesOf("timeZone");

  const [loading, setLoading] = useState(true);
  const [slide, setSlide] = useState(null);
  const [error, setError] = useState(null);
  const [timeZone, setTimeZone] = useState(
    Intl.DateTimeFormat().resolvedOptions().timeZone
  );

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true);
      setError(null);
      try {
        const data = await authedGet(`/customer/slides/${encodeURIComponent(jobId)}`);
        if (!cancelled) setSlide(data);
      } catch (err) {
        if (!cancelled) setError(err.message || "Failed to load slide");
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => {
      cancelled = true;
    };
  }, [jobId]);

  useEffect(() => {
    document.title = slide?.slide_identifier || PAGE_TITLE;
  }, [slide]);

  if (loading) {
    return (
      <div>
        <h3>Loading slide…</h3>
      </div>
    );
  }

  if (error) {
    return (
      <div>
        <h3 style={{ color: "red" }}>{error}</h3>
        <button type="button" onClick={() => navigate(-1)}>
          ← Back
        </button>
      </div>
    );
  }

  if (!slide) return null;

  return (
    <div style={{ fontSize: 15 }}>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 16,
          paddingBottom: 10,
          borderBottom: "2px solid #ddd",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <h1 style={{ margin: 0 }}>
            <strong>{slide.slide_identifier}</strong>
          </h1>
          <StatusBadge status={slide.status} size="large" />
        </div>
        <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
          <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
            <select
              value={timeZone}
              onChange={(e) => setTimeZone(e.target.value)}
              style={{ fontSize: 14 }}
            >
              {ALL_TIMEZONES.map((tz) => (
                <option key={tz} value={tz}>
                  {tz}
                </option>
              ))}
            </select>
            <InfoTip text={TIPS.timezone} align="end" />
          </span>
          <button type="button" onClick={() => navigate(-1)}>
            ← Back
          </button>
        </div>
      </div>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "1fr 1.2fr",
          gap: 16,
          marginBottom: 20,
        }}
      >
        <Section title="Summary">
          <KV tip={TIPS.application} label="Application" value={slide.application || "AIxURO"} />
          <KV tip={TIPS.origin} label="Origin" value={slide.request_origin || "—"} />
          <KV tip={TIPS.received} label="Received at" value={formatTimestamp(slide.start_datetime, timeZone)} />
          <KV tip={TIPS.finished} label="Finished at" value={formatTimestamp(slide.finished_at, timeZone)} />
          <KV
            tip={TIPS.duration}
            label="Duration"
            value={
              slide.job_duration_secs != null
                ? formatDurationHuman(slide.job_duration_secs)
                : "—"
            }
          />
          <KV
            tip={TIPS.viewer}
            label="Cytology Viewer Access"
            value={
              slide.aixcloud_cv_access_url ? (
                <a
                  href={slide.aixcloud_cv_access_url}
                  target="_blank"
                  rel="noreferrer"
                  style={{ color: "#1976d2", textDecoration: "none", fontWeight: 600 }}
                >
                  Launch in Viewer
                </a>
              ) : (
                "—"
              )
            }
          />
        </Section>

        <Section title="Slide Label">
          <div
            style={{
              maxHeight: 320,
              overflow: "hidden",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            {slide.presigned_label_image_url ? (
              <ZoomableImage
                src={slide.presigned_label_image_url}
                thumbMaxWidth={200}
                thumbMaxHeight={200}
              />
            ) : (
              <div style={{ color: "#777", fontStyle: "italic", fontSize: 14 }}>
                No label image available
              </div>
            )}
          </div>
        </Section>
      </div>

      <Section title="Status">
        <KV tip={TIPS.status} label="Status" value={<StatusBadge status={slide.status} />} />
        <KV tip={TIPS.message} label="Last Status Details" value={slide.message || "—"} />
      </Section>

      <Section title="Inference Results">
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
          <div>
            <h4 style={{ color: "#ed6c02", marginBottom: 6, display: "inline-flex", alignItems: "center", gap: 6 }}>
              Suspicious
              <InfoTip text={TIPS.suspicious} />
            </h4>
            <KV label="# Cells" value={slide.n_suspicious ?? 0} />
            <KV label="N/C Ratio" value={slide.suspicious_nc_ratio || "—"} />
            <KV label="Nucleus Area Ratio" value={slide.suspicious_nucleus_area || "—"} />
          </div>
          <div>
            <h4 style={{ color: "#1976d2", marginBottom: 6, display: "inline-flex", alignItems: "center", gap: 6 }}>
              Atypical
              <InfoTip text={TIPS.atypical} />
            </h4>
            <KV label="# Cells" value={slide.n_atypical ?? 0} />
            <KV label="N/C Ratio" value={slide.atypical_nc_ratio || "—"} />
            <KV label="Nucleus Area Ratio" value={slide.atypical_nucleus_area || "—"} />
          </div>
        </div>
      </Section>

      <Section title="Slide File / Imaging Details">
        <KV tip={TIPS.fileSize} label="File Size (MB)" value={slide.input_image_size_in_mbytes ?? "—"} />
        <KV tip={TIPS.layers} label="# Layers" value={slide.n_layers ?? 1} />
        <KV tip={TIPS.mpp} label="MPP" value={slide.mpp ?? "—"} />
        <KV tip={TIPS.magnification} label="Magnification" value={formatMagnification(slide.magnification)} />
        <KV tip={TIPS.scanner} label="Scanner" value={slide.scanner || "—"} />
        <KV tip={TIPS.med} label="MED format" value={formatMedFormatVersion(slide.med_format_version)} />
        <KV tip={TIPS.hash} label="File Hash" value={slide.input_file_hash || "—"} />
      </Section>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div
      style={{
        ...adminStyles.card,
        border: "1px solid #ccc",
        borderRadius: 8,
        marginBottom: 16,
        padding: 12,
      }}
    >
      <div
        style={{
          borderBottom: "2px solid #ddd",
          paddingBottom: 6,
          marginBottom: 10,
        }}
      >
        <h3 style={{ margin: 0, fontSize: 19, fontWeight: 700 }}>{title}</h3>
      </div>
      {children}
    </div>
  );
}

function KV({ label, value, tip }) {
  return (
    <div style={{ display: "flex", gap: 10, marginBottom: 4, fontSize: 15 }}>
      <strong
        style={{
          width: 210,
          display: "inline-flex",
          alignItems: "center",
          gap: 6,
          flexShrink: 0,
        }}
      >
        {label}
        {tip ? <InfoTip text={tip} /> : null}
      </strong>
      <span>{value}</span>
    </div>
  );
}
